import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type SortingState } from "@tanstack/react-table";
import {
  useCatalogSpecialNotices,
  type CatalogSpecialNoticesParams,
  type CatalogSpecialNoticeSortField,
} from "@/hooks/use-catalog-special-notices";
import { useSpecialNoticeColumns } from "@/components/catalog/special-notice-columns";
import {
  SpecialNoticeFilters,
  type SpecialNoticeFilterValues,
} from "@/components/catalog/special-notice-filters";
import {
  Breadcrumbs,
  DataTable,
  EmptyState,
  PageHeader,
  Pagination,
  SkeletonTable,
} from "@/components/ui";

export const Route = createFileRoute("/_authenticated/catalog/special-notices/")({
  component: CatalogSpecialNoticesPage,
});

const PAGE_SIZE = 25;

// Column ids that round-trip 1:1 to the API sort enum
// (notice_id | notice_year | status). `title` is sort-disabled in the
// column factory because the API does not expose it as a sort key.
const COLUMN_TO_SORT: Record<string, CatalogSpecialNoticeSortField> = {
  notice_id: "notice_id",
  notice_year: "notice_year",
  status: "status",
};

function CatalogSpecialNoticesPage() {
  const { t } = useTranslation(["catalog", "common", "nav"]);
  const navigate = useNavigate();
  const columns = useSpecialNoticeColumns();

  const [filters, setFilters] = useState<SpecialNoticeFilterValues>({});
  const [page, setPage] = useState(0);
  // Default newest-first matches the regulatory recency lens compliance officers
  // expect when scanning USC notices, plus the server's `notice_year DESC,
  // notice_id ASC` index path.
  const [sorting, setSorting] = useState<SortingState>([{ id: "notice_year", desc: true }]);

  const params = useMemo<CatalogSpecialNoticesParams>(() => {
    const first = sorting[0];
    const sortField = first ? COLUMN_TO_SORT[first.id] : undefined;
    return {
      q: filters.q,
      status: filters.status,
      sort: sortField,
      order: first ? (first.desc ? "desc" : "asc") : undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    };
  }, [filters, page, sorting]);

  const { data, isLoading, isError, error } = useCatalogSpecialNotices(params);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleSortingChange = (next: SortingState) => {
    setSorting(next);
    setPage(0);
  };

  return (
    <div className="p-8">
      <Breadcrumbs
        items={[
          { label: t("catalog.group", { ns: "nav" }) },
          { label: t("page.specialNoticesTitle") },
        ]}
      />
      <PageHeader title={t("page.specialNoticesTitle")} />

      <div className="mt-6">
        <SpecialNoticeFilters
          filters={filters}
          onChange={(next) => {
            setFilters(next);
            setPage(0);
          }}
        />
      </div>

      {isError && (
        <div className="mt-6 rounded-md bg-danger/10 p-4 text-danger">
          {error instanceof Error ? error.message : t("errors.loadFailed")}
        </div>
      )}

      {isLoading ? (
        <div className="mt-6">
          <SkeletonTable columns={4} rows={5} />
        </div>
      ) : items.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            title={t("page.specialNoticesTitle")}
            description={t("empty.specialNotices")}
          />
        </div>
      ) : (
        <>
          <div className="mt-6">
            <DataTable
              columns={columns}
              data={items}
              sorting={sorting}
              onSortingChange={handleSortingChange}
              onRowClick={(row) =>
                void navigate({
                  to: "/catalog/special-notices/$id",
                  params: { id: row.id },
                })
              }
            />
          </div>
          <Pagination
            currentPage={page + 1}
            totalPages={totalPages}
            totalItems={total}
            pageSize={PAGE_SIZE}
            onPageChange={(p) => setPage(p - 1)}
          />
        </>
      )}
    </div>
  );
}
